import { CloudUpload, KeyRound, LockKeyhole, ShieldCheck, Smartphone } from 'lucide-react';
import { useState } from 'react';

import { maskPhone, toE164 } from '@core/phone';
import { pinProblem } from '@core/pin';

import { Button } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { lastPhoneLast4, pinStatus, signIn } from '@/lib/store';

type Step = 'phone' | 'create' | 'confirm' | 'enter';

function friendly(e: unknown) {
  if (e instanceof ApiError) {
    const left = e.details?.remainingAttempts;
    return typeof left === 'number' ? `${e.message} ${left} attempts left.` : e.message;
  }
  return 'Something went wrong. Please try again.';
}

export function SignIn() {
  const [step, setStep] = useState<Step>('phone');
  const [phoneInput, setPhoneInput] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [first, setFirst] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  // Last-4 of the previously used number — read once; it only changes on sign-in.
  const [lastHint] = useState(lastPhoneLast4);
  const normalised = toE164(phoneInput);

  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try {
      await task();
    } catch (e) {
      setPin('');
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  };

  const go = (s: Step) => {
    setPin('');
    setError(undefined);
    setStep(s);
  };

  // On success the app switches to the signed-in layout (and asks for a name if missing).
  const complete = async (isNew: boolean, value: string) => {
    await signIn(phone, value, isNew);
  };

  const onPin = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setPin(digits);
    setError(undefined);
    if (digits.length < 6) return;
    if (step === 'create') {
      const problem = pinProblem(digits);
      if (problem) return (setPin(''), setError(problem));
      setFirst(digits);
      go('confirm');
    } else if (step === 'confirm') {
      if (digits !== first) {
        setFirst('');
        setStep('create');
        setPin('');
        return setError("PINs didn't match. Choose your PIN again.");
      }
      void run(() => complete(true, digits));
    } else if (step === 'enter') {
      void run(() => complete(false, digits));
    }
  };

  const titles: Record<Step, [string, string]> = {
    phone: ['Sign in to Spendly', 'Use your phone number and PIN. Same account as the app.'],
    create: ['Create a PIN', `Choose a 6-digit PIN for ${maskPhone(phone)}.`],
    confirm: ['Confirm your PIN', 'Enter the same 6 digits again.'],
    enter: ['Welcome back', `Enter the PIN for ${maskPhone(phone)}.`],
  };

  return (
    <div className="auth-wrap">
      <section className="auth-art">
        <div className="brand" style={{ color: '#fff', padding: 0 }}>
          <img src="/app/favicon.svg" alt="" />
          Spendly
        </div>
        <div>
          <h2>Know where your money goes.</h2>
          <p style={{ opacity: 0.85, maxWidth: 380 }}>
            Your expenses from the Spendly app, on a bigger screen. Add, search and analyse — changes sync to your phone automatically.
          </p>
        </div>
        <div className="grid" style={{ gap: 12, opacity: 0.9 }}>
          <div className="row"><Smartphone size={18} /> Works with the Android app</div>
          <div className="row"><CloudUpload size={18} /> Synced backup of every expense and receipt</div>
          <div className="row"><ShieldCheck size={18} /> PIN-protected, private by default</div>
        </div>
      </section>

      <section className="auth-form">
        <div className="auth-card">
          <div>
            <h1 style={{ margin: '0 0 6px', fontSize: 26, letterSpacing: -0.5 }}>{titles[step][0]}</h1>
            <p className="muted" style={{ margin: 0 }}>{titles[step][1]}</p>
          </div>

          {step === 'phone' && (
            <form
              className="grid"
              onSubmit={(e) => {
                e.preventDefault();
                if (!normalised) return;
                void run(async () => {
                  const s = await pinStatus(normalised);
                  setPhone(normalised);
                  go(s === 'existing' ? 'enter' : 'create');
                });
              }}>
              <label className="field">
                <span>Mobile number</span>
                <input
                  className={`input ${error ? 'invalid' : ''}`}
                  type="tel"
                  autoComplete="tel"
                  placeholder="98765 43210"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  autoFocus
                />
              </label>
              <span className="faint" style={{ fontSize: 13 }}>Indian numbers can be typed without +91.</span>
              {lastHint && (
                <span className="faint" style={{ fontSize: 13 }}>Previously used ••••• •{lastHint}</span>
              )}
              {error && <span className="error-text">{error}</span>}
              <Button size="lg" block disabled={!normalised} loading={busy} type="submit">
                Continue
              </Button>
            </form>
          )}

          {(step === 'create' || step === 'confirm' || step === 'enter') && (
            <div className="grid">
              <label className="field">
                <span className="row">
                  {step === 'enter' ? <LockKeyhole size={14} /> : <KeyRound size={14} />}
                  {step === 'confirm' ? 'Confirm PIN' : 'PIN'}
                </span>
                <input
                  key={step}
                  className={`input pin-input ${error ? 'invalid' : ''}`}
                  type="password"
                  inputMode="numeric"
                  autoComplete={step === 'enter' ? 'current-password' : 'new-password'}
                  maxLength={6}
                  value={pin}
                  disabled={busy}
                  onChange={(e) => onPin(e.target.value)}
                  autoFocus
                  aria-describedby="pin-help"
                />
              </label>
              {error ? (
                <span className="error-text">{error}</span>
              ) : (
                <span id="pin-help" className="faint" style={{ fontSize: 13 }}>
                  {step === 'enter' ? 'After 5 wrong tries the account locks for 15 minutes.' : 'Avoid easy PINs like 123456.'}
                </span>
              )}
              {busy && <span className="muted">Signing in…</span>}
              <Button variant="ghost" onClick={() => go('phone')}>
                Use a different number
              </Button>
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
